/*
  EVA-6393 homepage (sections/afhm-*.liquid, one per chapter). Every one of
  those sections includes this file; it runs once and sets up each section,
  with the page-wide parts (the visitor's pick, the sticky bar) read across
  all of them.

  - Video sheet: every [data-afhm-play] opens one shared <dialog>, filled from
    the trigger's data attributes. Closing empties it, which stops playback.
  - Remembering the visitor's dental work: picking a card in "Which are you
    cleaning around?" is kept for the visit (sessionStorage) and fills the
    [data-afhm-personal] lines, so later chapters speak to what they picked.
  - Sticky bar: shows once the hero's buttons have scrolled away, and steps
    aside whenever a chapter with its own buy button, or the footer, is on
    screen, so there are never two buy buttons in view.
  - Hero loop: muted, paused off screen, and not played at all when the
    visitor has asked for reduced motion.
  - Why we exist: the AutoFlosser's floss becomes the line above his name.
  - Sign-up: after Shopify's form posts back, bring the code into view.
  - Hero: a strand of floss hangs under the headline's highlighted words and
    gives like a real one when the cursor brushes it; the loop is captioned
    scene by scene.
  Desktop only (990px and up):
  - the AutoFlosser the floss leaves from, and the scene steps jump to a scene;
  - the case cards drive an inline stage instead of the sheet;
  - the kit's numbered pins and its legend light each other up.
*/
(function () {
  'use strict';
  /* Every homepage section includes this file; run it once. */
  if (window.__afhmLoaded) return;
  window.__afhmLoaded = true;

  var KEY = 'afhm-case';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function readCase() {
    try { return window.sessionStorage.getItem(KEY) || ''; } catch (e) { return ''; }
  }
  function saveCase(value) {
    try { window.sessionStorage.setItem(KEY, value); } catch (e) { /* private mode: the page still works */ }
  }

  function init(root) {
    if (!root || root.__afhmReady) return;
    root.__afhmReady = true;

    /* ---------- remembering their dental work ---------- */
    function personalise(caseName) {
      if (!caseName) return;
      /* across the whole page: the chapters are separate sections */
      document.querySelectorAll('[data-afhm-personal]').forEach(function (el) {
        var tmpl = el.getAttribute('data-afhm-personal');
        if (tmpl && tmpl.indexOf('[case]') !== -1) el.textContent = tmpl.replace('[case]', caseName);
      });
      document.querySelectorAll('.afhm-fitcard[data-case]').forEach(function (card) {
        card.classList.toggle('is-picked', card.getAttribute('data-case') === caseName);
      });
    }
    personalise(readCase());

    /* ---------- video sheet ---------- */
    var sheet = root.querySelector('[data-afhm-sheet]');
    var media = sheet && sheet.querySelector('[data-afhm-media]');
    var lastTrigger = null;

    function setText(sel, value) {
      var el = sheet.querySelector(sel);
      if (!el) return;
      el.textContent = value || '';
      el.hidden = !value;
    }

    function openSheet(trigger) {
      if (!sheet || typeof sheet.showModal !== 'function') return false;
      var d = trigger.dataset;
      media.innerHTML = '';
      media.style.removeProperty('--afhm-r');

      if (d.yt) {
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(d.yt) +
          '?autoplay=1&playsinline=1&rel=0&modestbranding=1';
        f.title = d.title || 'Video';
        f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        media.appendChild(f);
      } else if (d.mp4) {
        var v = document.createElement('video');
        v.controls = true;
        v.playsInline = true;
        v.autoplay = true;
        v.preload = 'auto';
        if (d.poster) v.poster = d.poster;
        v.src = d.mp4;
        if (d.ratio && parseFloat(d.ratio) > 0) media.style.setProperty('--afhm-r', String(parseFloat(d.ratio)));
        media.appendChild(v);
        var p = v.play();
        if (p && p.catch) p.catch(function () { /* autoplay with sound refused: controls are there */ });
      } else {
        return false;
      }

      setText('[data-afhm-kicker]', d.kicker);
      setText('[data-afhm-title]', d.title);
      setText('[data-afhm-text]', d.text);
      var tip = sheet.querySelector('[data-afhm-tip]');
      sheet.querySelector('[data-afhm-tip-text]').textContent = d.tip || '';
      tip.hidden = !d.tip;
      var guide = sheet.querySelector('[data-afhm-guide]');
      if (d.guide) {
        guide.href = d.guide;
        sheet.querySelector('[data-afhm-guide-label]').textContent = d.guideLabel || 'Read the full guide';
        guide.hidden = false;
      } else {
        guide.hidden = true;
      }

      if (d.case) {
        saveCase(d.case);
        personalise(d.case);
      }

      lastTrigger = trigger;
      document.documentElement.classList.add('afhm-locked');
      document.documentElement.style.overflow = 'hidden';
      sheet.showModal();
      return true;
    }

    function closeSheet() {
      if (sheet && sheet.open) sheet.close();
    }

    if (sheet) {
      sheet.addEventListener('close', function () {
        media.innerHTML = '';
        document.documentElement.classList.remove('afhm-locked');
        document.documentElement.style.overflow = '';
        if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
      });
      sheet.addEventListener('click', function (e) {
        if (e.target === sheet) closeSheet();
      });
      sheet.querySelector('[data-afhm-close]').addEventListener('click', closeSheet);
    }

    /* ---------- desktop: the cases stage ---------- */
    var desk = window.matchMedia('(min-width: 990px)');
    var stage = root.querySelector('[data-afhm-stage]');
    var stageMedia = stage && stage.querySelector('[data-afhm-stage-media]');
    var stageYt = '';

    function stageText(sel, value) {
      var el = stage.querySelector(sel);
      if (!el) return;
      el.textContent = value || '';
      el.hidden = !value;
    }

    function stageStop() {
      var f = stageMedia && stageMedia.querySelector('iframe');
      if (f) f.remove();
      if (stage) stage.classList.remove('is-playing');
    }

    function stageSelect(card, userPicked) {
      if (!stage || !card) return;
      var d = card.dataset;
      root.querySelectorAll('.afhm-fitcard').forEach(function (c) {
        var on = c === card;
        c.classList.toggle('is-staged', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      stageStop();
      stageYt = d.yt || '';
      var poster = stage.querySelector('[data-afhm-stage-poster]');
      if (stageYt) {
        poster.onerror = function () {
          poster.onerror = null;
          poster.src = 'https://i.ytimg.com/vi/' + stageYt + '/hqdefault.jpg';
        };
        poster.src = 'https://i.ytimg.com/vi/' + stageYt + '/maxresdefault.jpg';
      }
      stage.querySelector('[data-afhm-stage-play]').hidden = !stageYt;
      stageText('[data-afhm-stage-title]', d.title);
      stageText('[data-afhm-stage-by]', d.kicker);
      stageText('[data-afhm-stage-text]', d.text);
      stage.querySelector('[data-afhm-stage-tip-text]').textContent = d.tip || '';
      stage.querySelector('[data-afhm-stage-tip]').hidden = !d.tip;
      var guide = stage.querySelector('[data-afhm-stage-guide]');
      if (d.guide) {
        guide.href = d.guide;
        stage.querySelector('[data-afhm-stage-guide-label]').textContent = d.guideLabel || 'Read the full guide';
        guide.hidden = false;
      } else {
        guide.hidden = true;
      }
      stage.classList.remove('is-swap');
      void stage.offsetWidth;
      stage.classList.add('is-swap');
      if (userPicked && d.case) {
        saveCase(d.case);
        personalise(d.case);
      }
    }

    function stageInit() {
      if (!stage) return;
      if (!desk.matches) {
        stageStop();
        root.querySelectorAll('.afhm-fitcard').forEach(function (c) {
          c.removeAttribute('aria-pressed');
          c.classList.remove('is-staged');
        });
        return;
      }
      if (root.querySelector('.afhm-fitcard.is-staged')) return;
      var remembered = readCase();
      var cards = Array.prototype.slice.call(root.querySelectorAll('.afhm-fitcard'));
      var pick = cards.filter(function (c) { return remembered && c.getAttribute('data-case') === remembered; })[0] || cards[0];
      stageSelect(pick, false);
    }
    if (stage) {
      stage.querySelector('[data-afhm-stage-play]').addEventListener('click', function () {
        if (!stageYt) return;
        var f = document.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(stageYt) +
          '?autoplay=1&playsinline=1&rel=0&modestbranding=1';
        f.title = stage.querySelector('[data-afhm-stage-title]').textContent || 'Video';
        f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        stageMedia.appendChild(f);
        stage.classList.add('is-playing');
        f.focus();
      });
      stageInit();
      if (desk.addEventListener) desk.addEventListener('change', stageInit);
    }

    root.addEventListener('click', function (e) {
      var staged = e.target.closest('.afhm-fitcard');
      if (staged && stage && desk.matches) {
        e.preventDefault();
        stageSelect(staged, true);
        return;
      }
      var trigger = e.target.closest('[data-afhm-play]');
      if (trigger && root.contains(trigger)) {
        if (openSheet(trigger)) e.preventDefault();
        return;
      }
      var card = e.target.closest('.afhm-fitcard[data-case]');
      if (card && !card.hasAttribute('data-afhm-play')) {
        saveCase(card.getAttribute('data-case'));
        personalise(card.getAttribute('data-case'));
      }

      var jump = e.target.closest('[data-afhm-scroll]');
      if (jump) {
        var target = document.querySelector(jump.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
          var heading = target.querySelector('h2');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        }
      }

      var copy = e.target.closest('[data-afhm-copy]');
      if (copy) {
        var code = copy.getAttribute('data-afhm-copy');
        var label = copy.querySelector('[data-afhm-copy-label]');
        var done = function () {
          if (label) label.textContent = 'Copied';
          setTimeout(function () { if (label) label.textContent = 'Copy'; }, 2200);
        };
        var legacy = function () {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (err) { /* nothing more to try */ }
          ta.remove();
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, legacy);
        else legacy();
      }
    });

    /* ---------- sticky bar ---------- */
    var bar = root.querySelector('[data-afhm-sticky]');
    if (bar && 'IntersectionObserver' in window) {
      var barBtn = bar.querySelector('a');
      var heroCta = document.querySelector('[data-afhm-hero-cta]');
      var heroGone = !heroCta;
      var quietOnScreen = new Set();
      var update = function () {
        var on = heroGone && quietOnScreen.size === 0;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (barBtn) barBtn.tabIndex = on ? 0 : -1;
      };
      if (heroCta) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            heroGone = !en.isIntersecting;
          });
          update();
          /* The huge bottom margin means "not intersecting" can only mean
             scrolled above the top, however fast the visitor scrolled. */
        }, { rootMargin: '0px 0px 100000px 0px' }).observe(heroCta);
      }
      var quietIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) quietOnScreen.add(en.target);
          else quietOnScreen.delete(en.target);
        });
        update();
      }, { rootMargin: '0px 0px -12% 0px' });
      document.querySelectorAll('[data-afhm-quiet]').forEach(function (el) { quietIO.observe(el); });
      var footer = document.querySelector('.shopify-section-group-footer-group, footer');
      if (footer) quietIO.observe(footer);
      update();
    }

    /* ---------- hero loop ---------- */
    var loop = root.querySelector('[data-afhm-loop]');
    if (loop) {
      loop.muted = true;
      var wantPlay = function () { return !reduceMotion.matches; };
      if (!wantPlay()) {
        loop.removeAttribute('autoplay');
        loop.pause();
        /* held still, show a clear frame of the product rather than the
           opening blur */
        var still = parseFloat(loop.getAttribute('data-still'));
        if (still > 0) {
          var hold = function () { try { loop.currentTime = still; } catch (e) {} };
          if (loop.readyState >= 1) hold(); else loop.addEventListener('loadedmetadata', hold, { once: true });
        }
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && wantPlay()) {
              var pp = loop.play();
              if (pp && pp.catch) pp.catch(function () {});
            } else {
              loop.pause();
            }
          });
        }, { threshold: 0.15 }).observe(loop);
      }
      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', function () { if (!wantPlay()) loop.pause(); });
      }
    }

    /* ---------- the loop's captions and steps ---------- */
    var scenes = root.querySelector('[data-afhm-scenes]');
    if (scenes && loop) {
      var sceneBtns = Array.prototype.slice.call(scenes.querySelectorAll('[data-afhm-scene]'));
      var starts = sceneBtns.map(function (b) { return parseFloat(b.getAttribute('data-afhm-scene')) || 0; });
      var capEl = scenes.querySelector('[data-afhm-scene-cap]');
      var current = -1;
      var raf = 0;
      var tick = function () {
        if (!sceneBtns.length) return;
        var t = loop.currentTime || 0;
        var dur = loop.duration || (starts[starts.length - 1] + 5);
        var i = 0;
        for (var k = 0; k < starts.length; k++) if (t >= starts[k]) i = k;
        if (i !== current) {
          current = i;
          sceneBtns.forEach(function (b, k) {
            b.classList.toggle('is-on', k === i);
            b.classList.toggle('is-done', k < i);
            if (k === i) b.setAttribute('aria-current', 'step');
            else b.removeAttribute('aria-current');
            if (k !== i) b.style.setProperty('--p', k < i ? 1 : 0);
          });
          capEl.classList.remove('is-in');
          void capEl.offsetWidth;
          capEl.textContent = sceneBtns[i].getAttribute('data-cap');
          capEl.classList.add('is-in');
        }
        var end = i + 1 < starts.length ? starts[i + 1] : dur;
        var p = Math.min(1, Math.max(0, (t - starts[i]) / Math.max(0.1, end - starts[i])));
        sceneBtns[i].style.setProperty('--p', p.toFixed(3));
      };
      var frame = function () {
        tick();
        raf = !loop.paused ? requestAnimationFrame(frame) : 0;
      };
      loop.addEventListener('play', function () { if (!raf) raf = requestAnimationFrame(frame); });
      loop.addEventListener('seeked', tick);
      loop.addEventListener('loadedmetadata', tick);
      desk.addEventListener && desk.addEventListener('change', function () { if (!raf && !loop.paused) raf = requestAnimationFrame(frame); });
      sceneBtns.forEach(function (b, k) {
        b.addEventListener('click', function () {
          loop.currentTime = starts[k] + 0.05;
          tick();
        });
      });
      tick();
      if (!loop.paused) raf = requestAnimationFrame(frame);
    }

    /* ---------- a strand of floss under the headline ---------- */
    var floss = root.querySelector('[data-afhm-floss]');
    if (floss) {
      var fTitle = floss.parentElement;
      var fH1 = fTitle.querySelector('.afhm-hero__h1');
      var fEm = fH1 && fH1.querySelector('em');
      var fPaths = floss.querySelectorAll('[data-afhm-floss-path]');
      var fGrad = floss.querySelector('[data-afhm-floss-grad]');
      var fDev = fTitle.querySelector('[data-afhm-device]');
      /* the drawing is upright in a 160 x 1175 box, nose tip at (103.5, 9)
         from its top-left; it is turned about that point to lie almost level,
         body off the left edge of the screen, nose towards the words */
      var DEV_TX = (143.5 - 40) / 160, DEV_TY = (29 - 20) / 1175, DEV_TURN = 80;
      /* The strand is a string held at both ends: its points only move up and
         down, pulled level by their neighbours and down by their own weight,
         so at rest it hangs in a shallow curve, and brushed it ripples and
         settles. It sits behind the words, so the letters that dip below the
         line pass in front of it. */
      var N = 48, K = 0.3, DAMP = 0.035;
      var ys = new Float32Array(N + 1), vs = new Float32Array(N + 1);
      var fG = 0, fx0 = 0, fx1 = 0, fy0 = 0, fSize = 50;
      var fRaf = 0, fShown = false, fOn = true, fPtr = null, fBox = null;
      var restAt = function (i) { return fG / (2 * K) * i * (N - i); };
      var fLayout = function () {
        if (!fEm || !fTitle.offsetWidth) return false;
        var tr = fTitle.getBoundingClientRect();
        var rects = fEm.getClientRects();
        if (!rects.length || !tr.width) return false;
        var r = rects[rects.length - 1];
        fSize = parseFloat(getComputedStyle(fH1).fontSize) || 50;
        /* take in the full stop after the words, if there is one */
        var right = r.right;
        var next = fEm.nextSibling;
        if (next && next.nodeType === 3 && /^[.!?,]/.test(next.data)) {
          var rg = document.createRange();
          rg.setStart(next, 0);
          rg.setEnd(next, 1);
          var pr = rg.getBoundingClientRect();
          if (pr.width) right = pr.right;
        }
        /* the baseline: Montserrat's descent is about a quarter of the size */
        var base = r.bottom - tr.top - 0.251 * fSize;
        /* held up on both sides of the words, the way floss is held either
           side of a bridge, and hanging under them in between: it comes out
           from behind the first letter, passes behind the p and the g, and
           goes back behind the last */
        fx0 = r.left - tr.left - 0.9 * fSize;
        fx1 = right - tr.left + 0.9 * fSize;
        fy0 = base - 0.25 * fSize;
        fG = 0.55 * fSize * 8 * K / (N * N);
        /* the device shows only where the margin can hold a useful length of
           it; the floss then leaves its tip rather than fading in */
        var margin = tr.left + fx0;
        var withDev = !!fDev && margin >= 104;
        if (fDev) {
          fDev.classList.toggle('is-shown', withDev);
          if (withDev) {
            var len = 7.2 * fSize, wid = len * 160 / 1175;
            fDev.style.width = wid.toFixed(1) + 'px';
            fDev.style.height = len.toFixed(1) + 'px';
            fDev.style.left = (fx0 - wid * DEV_TX).toFixed(1) + 'px';
            fDev.style.top = (fy0 - len * DEV_TY).toFixed(1) + 'px';
            fDev.style.transformOrigin = (wid * DEV_TX).toFixed(1) + 'px ' + (len * DEV_TY).toFixed(1) + 'px';
            fDev.style.transform = 'rotate(' + DEV_TURN + 'deg)';
          }
        }
        if (fGrad) {
          fGrad.setAttribute('x1', fx0.toFixed(1));
          fGrad.setAttribute('x2', fx1.toFixed(1));
          fGrad.firstElementChild.setAttribute('stop-opacity', withDev ? '1' : '0');
        }
        return true;
      };
      var fDraw = function () {
        var dx = (fx1 - fx0) / N;
        var X = function (i) { return fx0 + dx * i; };
        var Y = function (i) { return fy0 + ys[i]; };
        var d = 'M' + X(0).toFixed(1) + ' ' + Y(0).toFixed(2);
        for (var i = 1; i < N; i++) {
          d += ' Q' + X(i).toFixed(1) + ' ' + Y(i).toFixed(2) + ' ' +
            ((X(i) + X(i + 1)) / 2).toFixed(1) + ' ' + ((Y(i) + Y(i + 1)) / 2).toFixed(2);
        }
        d += ' L' + X(N).toFixed(1) + ' ' + Y(N).toFixed(2);
        for (var p = 0; p < fPaths.length; p++) fPaths[p].setAttribute('d', d);
      };
      var fStep = function () {
        var dx = (fx1 - fx0) / N, reach = 0.42 * fSize, spread = 0.9 * fSize;
        for (var s = 0; s < 2; s++) {
          for (var i = 1; i < N; i++) {
            vs[i] += K * (ys[i - 1] + ys[i + 1] - 2 * ys[i]) + fG - DAMP * vs[i];
            if (fPtr) {
              var ax = 1 - Math.abs(fx0 + dx * i - fPtr.x) / spread;
              var gap = fy0 + ys[i] - fPtr.y;
              if (ax > 0 && Math.abs(gap) < reach) {
                var near = ax * (1 - Math.abs(gap) / reach);
                /* the cursor drags the strand the way it moves across it, and
                   a sideways sweep still nudges it away */
                vs[i] += (fPtr.vy * 0.07 + (gap >= 0 ? 1 : -1) * Math.abs(fPtr.vx) * 0.025) * near;
              }
            }
          }
          for (i = 1; i < N; i++) ys[i] += vs[i];
        }
        if (fPtr) { fPtr.vx *= 0.6; fPtr.vy *= 0.6; }
        var moving = 0;
        for (i = 1; i < N; i++) moving = Math.max(moving, Math.abs(vs[i]), Math.abs(ys[i] - restAt(i)) * 0.05);
        return moving > 0.004;
      };
      var fFrame = function () {
        var alive = fStep();
        fDraw();
        fRaf = (alive || (fPtr && (Math.abs(fPtr.vx) + Math.abs(fPtr.vy) > 0.05))) && fOn
          ? requestAnimationFrame(fFrame) : 0;
      };
      var fWake = function () { if (!fRaf) fRaf = requestAnimationFrame(fFrame); };
      var fShow = function () {
        if (!fLayout()) return;
        floss.classList.add('is-ready');
        if (fShown) { fDraw(); return; }
        fShown = true;
        if (reduceMotion.matches) {
          for (var i = 0; i <= N; i++) ys[i] = restAt(i);
          fDraw();
          return;
        }
        /* it is drawn through left to right, a little taut, then lets go and
           settles into its hang */
        for (var j = 0; j <= N; j++) ys[j] = restAt(j) * 0.7;
        fDraw();
        floss.classList.add('is-armed');
        void floss.getBoundingClientRect();
        requestAnimationFrame(function () {
          floss.classList.add('is-in');
          setTimeout(fWake, 500);
        });
      };
      var fReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      fReady.then(fShow);
      if ('ResizeObserver' in window) {
        var fPending = 0;
        new ResizeObserver(function () {
          cancelAnimationFrame(fPending);
          fPending = requestAnimationFrame(fShow);
        }).observe(fTitle);
      }
      if (desk.addEventListener) desk.addEventListener('change', fShow);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          fOn = entries[0].isIntersecting;
          if (fOn && fShown) fWake();
        }).observe(fTitle);
      }
      var fHero = floss.closest('[data-afhm-hero]') || fTitle;
      fHero.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !fShown || reduceMotion.matches || !desk.matches) return;
        if (!fBox || !fRaf) fBox = fTitle.getBoundingClientRect();
        var x = e.clientX - fBox.left, y = e.clientY - fBox.top;
        if (x < fx0 - 60 || x > fx1 + 60 || y < fy0 - 90 || y > fy0 + 0.6 * fSize + 90) { fPtr = null; return; }
        var clamp = function (n) { return Math.max(-24, Math.min(24, n)); };
        fPtr = fPtr
          ? { x: x, y: y, vx: clamp(x - fPtr.x), vy: clamp(y - fPtr.y) }
          : { x: x, y: y, vx: 0, vy: 0 };
        fWake();
      });
      fHero.addEventListener('pointerleave', function () { fPtr = null; });
      window.addEventListener('scroll', function () { fBox = null; }, { passive: true });
    }

    /* ---------- why we exist: the floss as the line he signs on ---------- */
    var sign = root.querySelector('[data-afhm-sign]');
    if (sign) {
      var sSec = sign.parentElement;
      var sWho = sSec.querySelector('.afhm-story__who');
      var sEx = sSec.querySelector('.afhm-exhibit');
      var sDev = sign.querySelector('[data-afhm-device]');
      var sPath = sign.querySelector('[data-afhm-sign-path]');
      var sGrad = sign.querySelector('[data-afhm-sign-grad]');
      var sDrawn = false;
      /* upright drawing, nose tip at (103.5, 9) of a 160 x 1175 box; stood up
         in the gap and mirrored so the nozzle leans towards his name */
      var S_TX = (143.5 - 40) / 160, S_TY = (29 - 20) / 1175;
      var sLayout = function () {
        if (!sWho || !sDev) return false;
        var sr = sSec.getBoundingClientRect();
        var wr = sWho.getBoundingClientRect();
        if (!wr.width) return false;
        var er = sEx ? sEx.getBoundingClientRect() : null;
        /* desktop: in the gap between his row and the patent; a phone: at the
           end of his row, in the room the stylesheet keeps there */
        var wide = desk.matches && er && er.width;
        if (wide && er.left - wr.right < 70) return false;
        /* desktop without the patent: at the end of his row, as on a phone,
           but at desktop scale and running out of the foot of the band */
        var solo = desk.matches && !wide;
        var f = function (n) { return n.toFixed(1); };
        var ruleY = wr.top - sr.top + 0.5;
        var left = wr.left - sr.left;
        var nx, ny, len, rise, reach, land;
        if (wide) {
          nx = (wr.right + er.left) / 2 - sr.left;
          /* if the paragraph's last line runs long, keep the arc low so it
             never crosses the words */
          ny = ruleY - 56;
          var sText = sSec.querySelector('.afhm-story__text');
          if (sText) {
            var sr2 = document.createRange();
            sr2.selectNodeContents(sText);
            var lines = sr2.getClientRects();
            var last = lines.length ? lines[lines.length - 1] : null;
            if (last && last.right - sr.left > nx - 250) ny = ruleY - 22;
          }
          /* long enough to run out of the foot of the band */
          len = Math.min(Math.max(360, sr.height - ny + 60), 460);
          rise = 64; reach = 120; land = nx - 210;
        } else if (solo) {
          nx = wr.right - sr.left - 30;
          ny = ruleY - 34;
          len = Math.min(Math.max(320, sr.height - ny + 50), 420);
          rise = 44; reach = 96; land = nx - 170;
        } else {
          nx = wr.right - sr.left - 24;
          ny = ruleY - 22;
          len = 300;
          rise = 26; reach = 64; land = nx - 118;
        }
        var wid = len * 160 / 1175;
        sDev.style.width = f(wid) + 'px';
        sDev.style.height = f(len) + 'px';
        sDev.style.left = f(nx - wid * S_TX) + 'px';
        sDev.style.top = f(ny - len * S_TY) + 'px';
        sDev.style.transformOrigin = f(wid * S_TX) + 'px ' + f(len * S_TY) + 'px';
        sDev.style.transform = 'rotate(-3deg) scaleX(-1)';
        sDev.classList.add('is-shown');
        /* out of the tip and upward, then one long fall that arrives level,
           so the strand settles onto the line rather than meeting it */
        sPath.setAttribute('d',
          'M' + f(nx) + ' ' + f(ny) +
          ' C' + f(nx - 2) + ' ' + f(ny - rise) + ' ' + f(nx - reach) + ' ' + f(ruleY) + ' ' + f(land) + ' ' + f(ruleY) +
          ' L' + f(left) + ' ' + f(ruleY));
        if (sGrad) {
          sGrad.setAttribute('x1', f(left));
          sGrad.setAttribute('x2', f(nx));
        }
        sSec.classList.add('has-sign');
        return true;
      };
      var sReveal = function (animate) {
        if (!sLayout()) {
          sSec.classList.remove('has-sign');
          return;
        }
        if (sDrawn) return;
        sDrawn = true;
        if (!animate || reduceMotion.matches) return;
        sign.classList.add('is-armed');
        void sign.getBoundingClientRect();
        requestAnimationFrame(function () { sign.classList.add('is-in'); });
      };
      var sReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      sReady.then(function () {
        sLayout();
        if ('IntersectionObserver' in window && !reduceMotion.matches) {
          /* arm it while it is still off screen, draw it when it arrives */
          if (sSec.getBoundingClientRect().top > window.innerHeight) {
            sign.classList.add('is-armed');
          }
          var sIO = new IntersectionObserver(function (entries) {
            if (!entries[0].isIntersecting) return;
            sIO.disconnect();
            if (sign.classList.contains('is-armed')) {
              sDrawn = true;
              requestAnimationFrame(function () { sign.classList.add('is-in'); });
            } else {
              sReveal(false);
            }
          }, { threshold: 0.35 });
          sIO.observe(sSec);
        } else {
          sReveal(false);
        }
      });
      if ('ResizeObserver' in window) {
        var sPending = 0;
        new ResizeObserver(function () {
          cancelAnimationFrame(sPending);
          sPending = requestAnimationFrame(function () { if (!sLayout()) sSec.classList.remove('has-sign'); });
        }).observe(sSec);
      }
      if (desk.addEventListener) desk.addEventListener('change', function () { if (!sLayout()) sSec.classList.remove('has-sign'); });
    }

    /* ---------- kit pins and the list light each other up ---------- */
    var kit = root.querySelector('[data-afhm-kit]');
    if (kit && kit.querySelector('[data-afhm-kit-pin]')) {
      var light = function (n) {
        kit.querySelectorAll('[data-afhm-kit-pin], [data-afhm-kit-item]').forEach(function (el) {
          var id = el.getAttribute('data-afhm-kit-pin') || el.getAttribute('data-afhm-kit-item');
          el.classList.toggle('is-lit', id === n);
        });
        kit.classList.toggle('has-lit', !!n);
      };
      kit.addEventListener('mouseover', function (e) {
        var el = e.target.closest('[data-afhm-kit-pin], [data-afhm-kit-item]');
        light(el ? (el.getAttribute('data-afhm-kit-pin') || el.getAttribute('data-afhm-kit-item')) : null);
      });
      kit.addEventListener('mouseleave', function () { light(null); });

      /* phones and tablets: the piece in view lights its pin, a tapped pin
         brings its piece round */
      var kitRow = kit.querySelector('[data-afhm-kit-row]');
      if (kitRow) {
        var kitItems = Array.prototype.slice.call(kitRow.querySelectorAll('[data-afhm-kit-item]'));
        var kitOn = function (n) {
          kit.querySelectorAll('[data-afhm-kit-pin], [data-afhm-kit-item]').forEach(function (el) {
            var id = el.getAttribute('data-afhm-kit-pin') || el.getAttribute('data-afhm-kit-item');
            el.classList.toggle('is-on', id === n);
          });
        };
        var steering = 0;
        var inView = function () {
          var pad = parseFloat(getComputedStyle(kitRow).paddingLeft) || 0;
          var best = kitItems[0], dist = Infinity;
          kitItems.forEach(function (li) {
            var d = Math.abs(li.offsetLeft - pad - kitRow.scrollLeft);
            if (d < dist) { dist = d; best = li; }
          });
          /* at the far end the last piece may never reach the start */
          if (kitRow.scrollLeft + kitRow.clientWidth >= kitRow.scrollWidth - 2) best = kitItems[kitItems.length - 1];
          return best;
        };
        var kitScrollT = 0;
        kitRow.addEventListener('scroll', function () {
          if (steering) return;
          cancelAnimationFrame(kitScrollT);
          kitScrollT = requestAnimationFrame(function () {
            var li = inView();
            if (li) kitOn(li.getAttribute('data-afhm-kit-item'));
          });
        }, { passive: true });
        kit.addEventListener('click', function (e) {
          var pin = e.target.closest('[data-afhm-kit-pin]');
          if (!pin || desk.matches) return;
          var n = pin.getAttribute('data-afhm-kit-pin');
          var li = kitRow.querySelector('[data-afhm-kit-item="' + n + '"]');
          if (!li) return;
          kitOn(n);
          var pad = parseFloat(getComputedStyle(kitRow).paddingLeft) || 0;
          clearTimeout(steering);
          steering = setTimeout(function () { steering = 0; }, 700);
          kitRow.scrollTo({ left: li.offsetLeft - pad, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
        kitOn('1');
      }
    }

    /* ---------- after signing up ---------- */
    var signed = root.querySelector('[data-afhm-done]');
    if (signed) {
      requestAnimationFrame(function () {
        signed.scrollIntoView({ block: 'center', behavior: 'auto' });
        signed.focus({ preventScroll: true });
      });
    }
  }

  document.querySelectorAll('[data-afhm]').forEach(init);
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target.querySelector('[data-afhm]'));
  });
})();
